import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { isDevAuthBypass, type User } from '@/lib/firebase'

export interface Comment {
  id: string
  slideIndex: number
  text: string
  userId: string
  displayName: string
  photoURL: string | null
  createdAt: Date | null
  resolved: boolean
  resolvedBy: string | null
  resolvedAt: Date | null
  parentId: string | null
}

export interface Thread {
  comment: Comment
  replies: Comment[]
}

function buildCommentDoc(user: User, slideIndex: number, text: string, parentId: string | null) {
  return {
    slideIndex,
    text: text.trim(),
    userId: user.uid,
    displayName: user.displayName || 'Anonymous',
    photoURL: user.photoURL || null,
    createdAt: serverTimestamp(),
    resolved: false,
    resolvedBy: null,
    resolvedAt: null,
    parentId,
  }
}

export function useComments(deckSlug: string, slideIndex: number) {
  const { user } = useAuth()
  const [comments, setComments] = useState<Comment[]>([])

  useEffect(() => {
    if (isDevAuthBypass) return
    const db = getFirestore()
    const commentsRef = collection(db, 'decks', deckSlug, 'comments')

    // Subscribe to comments for current slide
    const slideQ = query(
      commentsRef,
      where('slideIndex', '==', slideIndex),
      orderBy('createdAt', 'asc')
    )
    const unsubSlide = onSnapshot(slideQ, snap => {
      const items: Comment[] = snap.docs.map(d => {
        const data = d.data()
        return {
          id: d.id,
          slideIndex: data.slideIndex,
          text: data.text,
          userId: data.userId,
          displayName: data.displayName,
          photoURL: data.photoURL,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null,
          resolved: data.resolved ?? false,
          resolvedBy: data.resolvedBy ?? null,
          resolvedAt: data.resolvedAt instanceof Timestamp ? data.resolvedAt.toDate() : null,
          parentId: data.parentId ?? null,
        }
      })
      setComments(items)
    })

    return unsubSlide
  }, [deckSlug, slideIndex])

  const threads = useMemo(() => {
    const topLevel = comments.filter(c => c.parentId === null)
    const replyMap = new Map<string, Comment[]>()
    comments.forEach(c => {
      if (c.parentId !== null) {
        const arr = replyMap.get(c.parentId) || []
        arr.push(c)
        replyMap.set(c.parentId, arr)
      }
    })
    return topLevel.map(c => ({
      comment: c,
      replies: (replyMap.get(c.id) || []).sort((a, b) => {
        if (!a.createdAt) return -1
        if (!b.createdAt) return 1
        return a.createdAt.getTime() - b.createdAt.getTime()
      }),
    }))
  }, [comments])

  const addComment = useCallback(async (text: string) => {
    if (isDevAuthBypass) return
    if (!user || !text.trim()) return
    const db = getFirestore()
    const commentsRef = collection(db, 'decks', deckSlug, 'comments')
    await addDoc(commentsRef, buildCommentDoc(user, slideIndex, text, null))
  }, [user, deckSlug, slideIndex])

  const addReply = useCallback(async (parentId: string, text: string) => {
    if (isDevAuthBypass) return
    if (!user || !text.trim()) return
    const db = getFirestore()
    const commentsRef = collection(db, 'decks', deckSlug, 'comments')
    await addDoc(commentsRef, buildCommentDoc(user, slideIndex, text, parentId))
  }, [user, deckSlug, slideIndex])

  const deleteComment = useCallback(async (commentId: string) => {
    if (isDevAuthBypass) return
    const db = getFirestore()
    await deleteDoc(doc(db, 'decks', deckSlug, 'comments', commentId))
  }, [deckSlug])

  const resolveThread = useCallback(async (commentId: string) => {
    if (isDevAuthBypass) return
    if (!user) return
    const db = getFirestore()
    await updateDoc(doc(db, 'decks', deckSlug, 'comments', commentId), {
      resolved: true,
      resolvedBy: user.uid,
      resolvedAt: serverTimestamp(),
    })
  }, [user, deckSlug])

  const unresolveThread = useCallback(async (commentId: string) => {
    if (isDevAuthBypass) return
    if (!user) return
    const db = getFirestore()
    await updateDoc(doc(db, 'decks', deckSlug, 'comments', commentId), {
      resolved: false,
      resolvedBy: null,
      resolvedAt: null,
    })
  }, [user, deckSlug])

  return { threads, addComment, addReply, deleteComment, resolveThread, unresolveThread }
}
